import { apiClient } from './client'

/**
 * Программа обучения: библиотека шаблонов учителя (разделы, темы, элементы),
 * назначение ученику или группе снапшотом, закладка «где остановились» и тема
 * урока.
 *
 * Чтение работает без параметра подписки learning_programs_enabled (мягкая
 * деградация: назначения остаются видимыми), любое изменение — под ним.
 */

export type ProgramStatus = 'active' | 'archived'

/**
 * Шкала уровней программы — та же общепринятая шкала, что у поля «уровень» в
 * анкете ученика (`StudentFormFieldCatalogSeeder`).
 *
 * Список вынесен в одно место намеренно: свободный ввод уровня уже развёл в
 * базе латинскую 'A1' и кириллическую 'А1' — визуально одно значение, но ни
 * фильтр, ни группировка их не считают одинаковыми.
 */
export const PROGRAM_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const

/** Уровень программы; пустой уровень допустим — колонка nullable */
export type ProgramLevel = (typeof PROGRAM_LEVELS)[number]

export type ProgramElementType = 'theory' | 'vocabulary' | 'exercise'

export type ThemeStatus = 'planned' | 'in_progress' | 'done'

export type ThemeVisibility = 'hidden' | 'titles' | 'full'

export type AssignmentStatus = 'provisioning' | 'active' | 'paused' | 'completed'

export interface ProgramSection {
  id: number
  title: string
}

export interface ProgramElementWord {
  id: number
  text: string
  translation: string | null
  position: number
}

export interface ProgramThemeElement {
  id: number
  type: ProgramElementType
  position: number
  title: string | null
  body_json: Record<string, unknown> | null
  /** Элемент типа exercise ссылается на само задание */
  exercise_id?: number | null
  /** Тип и статус задания для строки списка блоков; содержимого здесь нет */
  exercise?: { id: number; type: string; status: string; title: string } | null
  words?: ProgramElementWord[]
}

export interface ProgramTheme {
  id: number
  section_id: number | null
  title: string
  summary: string | null
  cover_url: string | null
  position: number
  content_version: number
  elements_count?: number
  elements?: ProgramThemeElement[]
}

export interface Program {
  id: number
  name: string
  level: string | null
  description: string | null
  cover_url: string | null
  status: ProgramStatus
  /** Программа платформы: можно назначить и скопировать, но не править */
  is_system: boolean
  /** Автообновление копий по умолчанию — значение для новых назначений */
  auto_update_default?: boolean
  /** Стабильный ключ системной программы; у программ учителя пуст */
  slug?: string | null
  themes_count?: number
  assignments_count?: number
  sections?: ProgramSection[]
  themes?: ProgramTheme[]
  created_at: string | null
}

/** Слово словаря темы в назначении: текст — снимок, действия — только при is_interactive */
export interface AssignedElementWord {
  id: number
  text: string
  translation: string | null
  transcription: string | null
  position: number
  word_id: number | null
  is_interactive: boolean
}

export interface AssignedThemeElement {
  id: number
  type: ProgramElementType
  position: number
  title: string | null
  body_json: Record<string, unknown> | null
  /** Копия задания в назначении: ученик решает её, а не шаблон учителя */
  exercise_id?: number | null
  completed_at: string | null
  /** Блок правили под этого ученика — подтяжка из каталога его обходит */
  is_customized?: boolean
  words?: AssignedElementWord[]
}

export interface AssignedTheme {
  id: number
  position: number
  status: ThemeStatus
  is_archived: boolean
  /** true — тема скрыта политикой видимости: есть только позиция и статус */
  hidden?: boolean
  title: string | null
  summary?: string | null
  cover_url?: string | null
  section_id?: number | null
  /** Название раздела темы — для группировки в списке выбора */
  section_title?: string | null
  completed_at?: string | null
  /** Тему правили вручную — автообновление её больше не трогает */
  is_customized?: boolean
  has_source_updates?: boolean
  elements?: AssignedThemeElement[]
}

export interface AssignmentStudentRow {
  student_id: number
  /** Приходит только в карточке назначения — в списке состав не грузится */
  name?: string | null
  joined_at: string | null
  left_at: string | null
  visibility: ThemeVisibility | null
}

export interface ProgramAssignment {
  id: number
  program_id: number | null
  name: string
  status: AssignmentStatus
  visibility_default: ThemeVisibility
  /** Правки программы доезжают до копии сами, пока её не начали и не правили */
  auto_update_enabled?: boolean
  recipient: {
    type: 'student' | 'group'
    id: number
    name?: string | null
  }
  bookmark: {
    theme_id: number | null
    element_id: number | null
    /** Название темы закладки — ответ на «на чём остановились» в списке */
    theme_title?: string | null
  }
  /**
   * Прогресс назначения целиком. У группы это её общий путь — личный путь
   * участника лежит в `personal` и с ним не совпадает.
   */
  group_progress: {
    themes_total?: number
    themes_done?: number
  }
  /**
   * Личный прогресс смотрящего ученика в групповом назначении. null у учителя
   * и у индивидуального назначения, где личный путь и путь назначения — одно.
   */
  personal?: {
    themes_done: number
    missed: number
    caught_up: number
  } | null
  themes?: AssignedTheme[]
  students?: AssignmentStudentRow[]
  completed_at: string | null
  created_at: string | null
}

export interface LessonThemePayload {
  /** Набор тем урока в порядке закрепления: за занятие бывает и две темы */
  themes: AssignedTheme[]
  /** Последняя тема набора — совместимость со старым контрактом */
  theme: AssignedTheme | null
  is_editable: boolean
  /** Назначение темы — вход из урока в полную карточку программы (только учителю) */
  assignment_id?: number | null
  /** Список для выбора отдаётся только учителю урока */
  available_themes: AssignedTheme[] | null
}

interface Paginated<T> {
  data: T[]
  meta?: { total?: number; current_page?: number; per_page?: number; last_page?: number }
}

/** Сводка «кого затронет правка темы каталога» */
export interface ThemePropagation {
  assignments_total: number
  auto_update_count: number
  manual_count: number
}

/**
 * Строка окна перед удалением темы или раздела шаблона: назначение, у которого
 * есть копия удаляемого и до которого доезжает структура шаблона
 * (автообновление, назначение не завершено, договор активен).
 */
export interface ProgramDeletionImpactItem {
  assignment_id: number
  /** Завершённые и готовящиеся назначения в окно не попадают */
  assignment_status: Extract<AssignmentStatus, 'active' | 'paused'>
  recipient: {
    type: 'student' | 'group'
    id: number
    /** ФИО ученика (без ФИО — почта или «Ученик #id») или название группы */
    name: string | null
  }
  /** Состояние копии темы у ученика; у раздела — null */
  theme_status: ThemeStatus | null
  /** Видимые темы в копии раздела; у темы — null */
  themes_count: number | null
}

/**
 * Тело DELETE темы или раздела: без него (или с пустым списком) удаляется
 * только шаблон, копии учеников не трогаются.
 */
const nodeDeletionBody = (assignmentIds?: number[]) =>
  assignmentIds === undefined ? undefined : { data: { assignment_ids: assignmentIds } }

export const programsApi = {
  async list(
    params: {
      status?: ProgramStatus
      q?: string
      /** Источник: свои шаблоны или готовые программы платформы */
      source?: 'own' | 'system'
      page?: number
      per_page?: number
    } = {},
  ): Promise<Paginated<Program>> {
    const { data } = await apiClient.get<Paginated<Program>>('/programs', { params })
    return data
  },

  /**
   * Все доступные программы, а не первая страница.
   *
   * `GET /programs` отдаёт максимум сотню за раз (потолок `pagination.max_per_page`),
   * а выбор программы ищет по уже загруженным опциям: со сто первой
   * программы назначить её из карточки ученика было нельзя вовсе — её нет
   * ни в списке, ни в поиске по нему. Обходим страницы тем же способом,
   * что `usersApi.listAllStudents` и `listAllGroups` в конструкторе программы.
   */
  async listAll(
    params: { status?: ProgramStatus; q?: string; source?: 'own' | 'system' } = {},
  ): Promise<Program[]> {
    const perPage = 100
    const programs: Program[] = []
    let page = 1
    let lastPage = 1

    do {
      const response = await programsApi.list({ ...params, per_page: perPage, page })
      programs.push(...(response.data ?? []))
      lastPage = response.meta?.last_page ?? 1
      page += 1
      // Страховка от зацикливания, если бэкенд вернёт некорректный last_page
    } while (page <= lastPage && page <= 100)

    return programs
  },

  async get(id: number): Promise<Program> {
    const { data } = await apiClient.get<{ data: Program }>(`/programs/${id}`)
    return data.data
  },

  async create(payload: {
    name: string
    level?: string
    description?: string
    auto_update_default?: boolean
  }): Promise<Program> {
    const { data } = await apiClient.post<{ data: Program }>('/programs', payload)
    return data.data
  },

  async update(
    id: number,
    payload: Partial<Pick<Program, 'name' | 'level' | 'description' | 'auto_update_default'>>,
  ): Promise<Program> {
    const { data } = await apiClient.patch<{ data: Program }>(`/programs/${id}`, payload)
    return data.data
  },

  async archive(id: number): Promise<Program> {
    const { data } = await apiClient.delete<{ data: Program }>(`/programs/${id}`)
    return data.data
  },

  /** Возврат из архива — отдельная операция: она упирается в лимит программ */
  async restore(id: number): Promise<Program> {
    const { data } = await apiClient.post<{ data: Program }>(`/programs/${id}/restore`)
    return data.data
  },

  async duplicate(id: number): Promise<Program> {
    const { data } = await apiClient.post<{ data: Program }>(`/programs/${id}/duplicate`)
    return data.data
  },

  async createSection(programId: number, title: string): Promise<ProgramSection> {
    const { data } = await apiClient.post<{ data: ProgramSection }>(`/programs/${programId}/sections`, { title })
    return data.data
  },

  async updateSection(programId: number, sectionId: number, title: string): Promise<ProgramSection> {
    const { data } = await apiClient.patch<{ data: ProgramSection }>(
      `/programs/${programId}/sections/${sectionId}`,
      { title },
    )
    return data.data
  },

  /** Кого затронет правка темы: сколько копий обновится само, сколько правили руками */
  async themePropagation(programId: number, themeId: number): Promise<ThemePropagation> {
    const { data } = await apiClient.get<{ data: ThemePropagation }>(
      `/programs/${programId}/themes/${themeId}/propagation`,
    )
    return data.data
  },

  /** Кого затронет удаление раздела: назначения, где у ученика есть его копия */
  async sectionDeletionImpact(programId: number, sectionId: number): Promise<ProgramDeletionImpactItem[]> {
    const { data } = await apiClient.get<{ data: ProgramDeletionImpactItem[] }>(
      `/programs/${programId}/sections/${sectionId}/deletion-impact`,
    )
    return data.data
  },

  /**
   * Удалить раздел шаблона. Темы раздела остаются в программе без раздела.
   *
   * `assignmentIds` — назначения из предпросмотра, у которых раздел убрать и в
   * копии (темы у ученика тоже остаются, без раздела). Возвращает, у скольких
   * назначений раздел действительно убран.
   */
  async deleteSection(programId: number, sectionId: number, assignmentIds?: number[]): Promise<number> {
    const { data } = await apiClient.delete<{ data?: { removed_from_assignments?: number } }>(
      `/programs/${programId}/sections/${sectionId}`,
      nodeDeletionBody(assignmentIds),
    )
    return data.data?.removed_from_assignments ?? 0
  },

  async createTheme(
    programId: number,
    payload: { title: string; summary?: string; section_id?: number | null },
  ): Promise<ProgramTheme> {
    const { data } = await apiClient.post<{ data: ProgramTheme }>(`/programs/${programId}/themes`, payload)
    return data.data
  },

  async updateTheme(
    programId: number,
    themeId: number,
    payload: { title?: string; summary?: string | null; section_id?: number | null },
  ): Promise<ProgramTheme> {
    const { data } = await apiClient.patch<{ data: ProgramTheme }>(`/programs/${programId}/themes/${themeId}`, payload)
    return data.data
  },

  /** Кого затронет удаление темы и в каком состоянии её копия у каждого */
  async themeDeletionImpact(programId: number, themeId: number): Promise<ProgramDeletionImpactItem[]> {
    const { data } = await apiClient.get<{ data: ProgramDeletionImpactItem[] }>(
      `/programs/${programId}/themes/${themeId}/deletion-impact`,
    )
    return data.data
  },

  /**
   * Удалить тему шаблона.
   *
   * `assignmentIds` — назначения из предпросмотра, у которых копию убрать так
   * же, как «Убрать из программы» (прогресс и журнал остаются). Возвращает, у
   * скольких назначений тема действительно убрана.
   */
  async deleteTheme(programId: number, themeId: number, assignmentIds?: number[]): Promise<number> {
    const { data } = await apiClient.delete<{ data?: { removed_from_assignments?: number } }>(
      `/programs/${programId}/themes/${themeId}`,
      nodeDeletionBody(assignmentIds),
    )
    return data.data?.removed_from_assignments ?? 0
  },

  /** Порядок и разделы тем одним запросом: список должен быть полным */
  async reorderThemes(programId: number, items: Array<{ id: number; section_id: number | null }>): Promise<Program> {
    const { data } = await apiClient.post<{ data: Program }>(`/programs/${programId}/themes/reorder`, { items })
    return data.data
  },

  async createElement(
    themeId: number,
    payload: {
      type: Exclude<ProgramElementType, 'exercise'>
      title?: string | null
      body_json?: Record<string, unknown>
      word_ids?: number[]
    },
  ): Promise<ProgramThemeElement> {
    const { data } = await apiClient.post<{ data: ProgramThemeElement }>(`/themes/${themeId}/elements`, payload)
    return data.data
  },

  /** Тип элемента неизменяем: поле, которого нет в запросе, сервер не трогает */
  async updateElement(
    themeId: number,
    elementId: number,
    payload: { title?: string | null; body_json?: Record<string, unknown>; word_ids?: number[] },
  ): Promise<ProgramThemeElement> {
    const { data } = await apiClient.patch<{ data: ProgramThemeElement }>(
      `/themes/${themeId}/elements/${elementId}`,
      payload,
    )
    return data.data
  },

  async deleteElement(themeId: number, elementId: number): Promise<void> {
    await apiClient.delete(`/themes/${themeId}/elements/${elementId}`)
  },

  async reorderElements(themeId: number, items: Array<{ id: number }>): Promise<ProgramTheme> {
    const { data } = await apiClient.post<{ data: ProgramTheme }>(`/themes/${themeId}/elements/reorder`, { items })
    return data.data
  },

  /** Агрегат по всем назначениям шаблона: где буксуют все группы сразу */
  async statistics(id: number): Promise<ProgramStatistics> {
    const { data } = await apiClient.get<{ data: ProgramStatistics }>(`/programs/${id}/statistics`)
    return data.data
  },
}

export interface AssignmentStatisticsTheme {
  theme_id: number
  position: number
  title: string
  status: ThemeStatus
  /** Средний ниже порога при трёх и более зачётных работах */
  is_pit: boolean
  average_percent: number | null
  graded_count: number
  awaiting_review: number
  students_with_result: number
  homework_total: number
  homework_done: number
  /** Сколько занятий заняла тема — достоверная цифра */
  lessons_count: number
  /** Минуты по плановым слотам; урок с двумя темами делится поровну */
  minutes: number
  time_source: 'planned'
}

export interface AssignmentStatisticsStudent {
  student_id: number
  name: string | null
  /** Вышел из группы: результаты заморожены, но остаются видны */
  left: boolean
  average_percent: number | null
  graded_count: number
  awaiting_review: number
  homework_total: number
  homework_done: number
  missed_themes: number
  caught_up_themes: number
  attendance_unknown: number
  below_average: boolean
}

export interface AssignmentStatistics {
  assignment_id: number
  is_group: boolean
  /** personal — ученик смотрит свой срез: в средних только его работы */
  scope: 'assignment' | 'personal'
  themes_total: number
  themes_done: number
  average_percent: number | null
  graded_count: number
  awaiting_review: number
  homework_total: number
  homework_done: number
  pit_threshold: number
  themes: AssignmentStatisticsTheme[]
  students: AssignmentStatisticsStudent[]
}

export interface ProgramStatisticsTheme {
  /** null — тема без источника: добавлена в назначение или источник удалён */
  source_theme_id: number | null
  title: string
  assignments_count: number
  average_percent: number | null
  graded_count: number
  is_pit: boolean
}

export interface ProgramStatistics {
  program_id: number
  assignments_total: number
  assignments_active: number
  average_percent: number | null
  graded_count: number
  pit_threshold: number
  themes: ProgramStatisticsTheme[]
}

export const programAssignmentsApi = {
  async list(
    params: { status?: AssignmentStatus; student_id?: number; page?: number; per_page?: number } = {},
  ): Promise<Paginated<ProgramAssignment>> {
    const { data } = await apiClient.get<Paginated<ProgramAssignment>>('/program-assignments', { params })
    return data
  },

  async get(id: number): Promise<ProgramAssignment> {
    const { data } = await apiClient.get<{ data: ProgramAssignment }>(`/program-assignments/${id}`)
    return data.data
  },

  async assign(
    programId: number,
    payload: {
      recipient_type: 'student' | 'group'
      recipient_id: number
      visibility_default?: ThemeVisibility
      auto_update_enabled?: boolean
    },
  ): Promise<ProgramAssignment> {
    const { data } = await apiClient.post<{ data: ProgramAssignment }>(`/programs/${programId}/assign`, payload)
    return data.data
  },

  async update(
    id: number,
    payload: {
      name?: string
      visibility_default?: ThemeVisibility
      status?: AssignmentStatus
      auto_update_enabled?: boolean
    },
  ): Promise<ProgramAssignment> {
    const { data } = await apiClient.patch<{ data: ProgramAssignment }>(`/program-assignments/${id}`, payload)
    return data.data
  },

  async setStudentVisibility(
    id: number,
    studentId: number,
    visibility: ThemeVisibility | null,
  ): Promise<ProgramAssignment> {
    const { data } = await apiClient.patch<{ data: ProgramAssignment }>(
      `/program-assignments/${id}/students/${studentId}`,
      { visibility },
    )
    return data.data
  },

  async moveBookmark(
    id: number,
    payload: { assigned_theme_id: number; assigned_theme_element_id?: number | null },
  ): Promise<ProgramAssignment> {
    const { data } = await apiClient.post<{ data: ProgramAssignment }>(`/program-assignments/${id}/bookmark`, payload)
    return data.data
  },

  /**
   * Отметить тему пройденной.
   *
   * completedAt (`YYYY-MM-DD`, не позже сегодня) — день прохождения, когда
   * учитель переносит историю давнего ученика. Без него тема проходится
   * текущим моментом. У уже пройденной темы день только исправляет дату:
   * закладка остаётся на месте, уведомления нет. Отметка прошедшим днём
   * уведомление ученику тоже не шлёт.
   */
  async completeTheme(id: number, themeId: number, completedAt?: string): Promise<ProgramAssignment> {
    const { data } = await apiClient.post<{ data: ProgramAssignment }>(
      `/program-assignments/${id}/themes/${themeId}/complete`,
      completedAt === undefined ? undefined : { completed_at: completedAt },
    )
    return data.data
  },

  async reopenTheme(id: number, themeId: number): Promise<ProgramAssignment> {
    const { data } = await apiClient.post<{ data: ProgramAssignment }>(
      `/program-assignments/${id}/themes/${themeId}/reopen`,
    )
    return data.data
  },

  /** Архивация темы копии: тема скрывается, история прохождения остаётся */
  async archiveTheme(id: number, themeId: number): Promise<ProgramAssignment> {
    const { data } = await apiClient.delete<{ data: ProgramAssignment }>(
      `/program-assignments/${id}/themes/${themeId}`,
    )
    return data.data
  },

  /** Подтянуть правки темы из шаблона: локальные изменения копии сохраняются */
  async pullThemeFromSource(id: number, themeId: number): Promise<AssignedTheme> {
    const { data } = await apiClient.post<{ data: AssignedTheme }>(
      `/program-assignments/${id}/themes/${themeId}/pull-from-source`,
    )
    return data.data
  },

  /**
   * Отметка «догнал»: ученик разобрал тему, пройденную группой без него.
   *
   * Экрана под неё пока нет и быть не может: поставить отметку осмысленно
   * только там, где видно, какую тему кто пропустил, а `attendance` темы
   * сервер наружу не отдаёт (`AssignedThemeResource::withAttendance()` никто
   * не вызывает). Метод оставлен как готовая привязка к рабочему эндпоинту —
   * первым шагом фичи бэкенд должен начать отдавать `attendance`.
   */
  async markCaughtUp(id: number, themeId: number, studentId: number, caughtUp: boolean): Promise<void> {
    await apiClient.post(
      `/program-assignments/${id}/themes/${themeId}/students/${studentId}/caught-up`,
      { caught_up: caughtUp },
    )
  },

  async completeElement(elementId: number, completed: boolean): Promise<void> {
    await apiClient.patch(`/assigned-theme-elements/${elementId}/complete`, { completed })
  },

  /**
   * Правка копии, назначенной ученику.
   *
   * Шаблон при этом не меняется: копия и заводилась ради того, чтобы подогнать
   * программу под конкретного ученика, не трогая то, что проходят остальные.
   */
  async createAssignedElement(
    themeId: number,
    payload: { type: 'theory' | 'vocabulary'; title?: string | null; body_json?: Record<string, unknown>; word_ids?: number[] },
  ): Promise<void> {
    await apiClient.post(`/assigned-themes/${themeId}/elements`, payload)
  },

  async updateAssignedElement(
    elementId: number,
    payload: { title?: string | null; body_json?: Record<string, unknown>; word_ids?: number[] },
  ): Promise<void> {
    await apiClient.patch(`/assigned-theme-elements/${elementId}`, payload)
  },

  async deleteAssignedElement(elementId: number): Promise<void> {
    await apiClient.delete(`/assigned-theme-elements/${elementId}`)
  },

  /** Учителю — назначение целиком, ученику — его личный срез */
  async statistics(id: number): Promise<AssignmentStatistics> {
    const { data } = await apiClient.get<{ data: AssignmentStatistics }>(`/program-assignments/${id}/statistics`)
    return data.data
  },
}

export const lessonThemeApi = {
  /**
   * adminReview — явный админский разбор урока: сервер считает роль по уроку,
   * и без флага админ-участник получает ученическую сторону (is_editable=false).
   */
  async get(lessonId: number, adminReview = false): Promise<LessonThemePayload> {
    const { data } = await apiClient.get<{ data: LessonThemePayload }>(`/lessons/${lessonId}/theme`, {
      params: adminReview ? { admin_review: 1 } : undefined,
    })
    return data.data
  },

  async set(lessonId: number, assignedThemeId: number): Promise<AssignedTheme | null> {
    const { data } = await apiClient.put<{ data: { theme: AssignedTheme | null } }>(`/lessons/${lessonId}/theme`, {
      assigned_theme_id: assignedThemeId,
    })
    return data.data.theme
  },

  async detach(lessonId: number): Promise<void> {
    await apiClient.delete(`/lessons/${lessonId}/theme`)
  },

  /** Добавить тему к уроку, не снимая остальных */
  async addTheme(lessonId: number, assignedThemeId: number): Promise<LessonThemePayload> {
    const { data } = await apiClient.post<{ data: LessonThemePayload }>(`/lessons/${lessonId}/themes`, {
      assigned_theme_id: assignedThemeId,
    })
    return data.data
  },

  /** Снять одну тему, оставив остальные */
  async removeTheme(lessonId: number, assignedThemeId: number): Promise<LessonThemePayload> {
    const { data } = await apiClient.delete<{ data: LessonThemePayload }>(
      `/lessons/${lessonId}/themes/${assignedThemeId}`,
    )
    return data.data
  },
}

/**
 * Системные программы платформы: каталог и создание.
 *
 * Правка тем, элементов и заданий идёт обычными методами `programsApi` — Policy
 * пускает туда админа, и второго набора вызовов не нужно.
 */
export const adminProgramsApi = {
  async list(params: { page?: number; per_page?: number } = {}): Promise<Paginated<Program>> {
    const { data } = await apiClient.get<Paginated<Program>>('/admin/programs', { params })
    return data
  },

  async create(payload: {
    name: string
    level?: string | null
    description?: string | null
    /** Стабильный ключ сидера: по нему он отличает уже созданную программу от новой */
    slug?: string | null
  }): Promise<Program> {
    const { data } = await apiClient.post<{ data: Program }>('/admin/programs', payload)
    return data.data
  },
}
